/**
 * App Layout Component
 *
 * Main application shell with sidebar and header.
 * Features:
 * - Responsive layout with collapsible sidebar
 * - Fixed header with navigation
 * - Main content area with outlet for nested routes
 * - Mobile-friendly with hamburger menu
 */

import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePaymentNotifications } from "@/hooks/usePaymentNotifications";
import { useVmDeletionNotifications } from "@/hooks/useVmDeletionNotifications";
import { SupportWidget } from "@/components/common/SupportWidget";
import { AgreementGate } from "@/components/agreements/AgreementGate";

const SIDEBAR_COLLAPSED_KEY = "wicloud_sidebar_collapsed";

export function AppLayout() {
  useDocumentTitle();
  // Mounted here rather than on any payment screen: a bank transfer is approved long after
  // the customer left the deposit flow, and there is no payment screen on that route at all.
  usePaymentNotifications();
  // Same reason: n8n confirms or refuses a VM deletion in the background, usually after the
  // customer has moved on to a page with no resource listener.
  useVmDeletionNotifications();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === "true";
  });

  useEffect(() => {
    const handleSidebarToggle = (e: Event) => {
      setSidebarCollapsed((e as CustomEvent).detail.collapsed);
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SIDEBAR_COLLAPSED_KEY && e.newValue !== null) {
        setSidebarCollapsed(e.newValue === "true");
      }
    };

    window.addEventListener("sidebar-toggle", handleSidebarToggle);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("sidebar-toggle", handleSidebarToggle);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    // Two different shells, on purpose (see the matching note in `index.css`):
    //
    // - >=md: a viewport-height, `overflow-hidden` frame so the sidebar stays pinned and
    //   only <main> scrolls.
    // - <md: no fixed height and no inner scroller — the *document* scrolls. Pinning the
    //   shell to a viewport unit on mobile is what made the end of long pages unreachable:
    //   the browser bar keeps resizing the viewport, and since the document could not
    //   scroll the bar never retracted, so any mismatch left the bottom of the inner
    //   scroll area permanently off-screen. `dvh` narrowed that gap but did not close it.
    <div className="min-h-dvh bg-background flex md:h-dvh md:overflow-hidden">
      {/* Sidebar - Hidden on mobile, visible on desktop */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Right side: Header + Main Content */}
      <div
        className={cn(
          // `min-w-0`: this is a flex item, so its automatic minimum size is the
          // min-content width of the page inside it. Without the override, one wide
          // child (a table, a chart) stretches the whole shell — header included —
          // past the viewport instead of scrolling inside its own box.
          "flex-1 min-w-0 flex flex-col transition-all duration-200 md:overflow-hidden",
          "md:ml-[248px]",
          sidebarCollapsed && "md:ml-16",
        )}
      >
        {/* Header */}
        <Header sidebarCollapsed={sidebarCollapsed} />

        {/* Main Content */}
        {/* `md:overflow-y-auto`: below md the document is the scroller, so <main> must
            stay a plain block — a nested scroller here would reintroduce the trapped
            scroll area. `pb-[env(safe-area-inset-bottom)]` keeps the last row clear of
            the phone's home indicator. */}
        <main className="flex-1 md:overflow-y-auto">
          <div className="w-full p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:p-6 md:pb-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Fixed to the viewport, so it stays clear of the shell's scroll containers. */}
      {/* Takes over the screen while the tenant owes the sign-up commitment. Mounted here
          rather than per page: the gate applies to the whole console, not one route. */}
      <AgreementGate />
      <SupportWidget />
    </div>
  );
}
